import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { apiClient } from "@/lib/api/client";
import type { AddressBookEntry } from "@/lib/api/types";
import { BookOpen, CheckCircle2, Star } from "lucide-react";
import * as React from "react";

type AddressFields = {
  address: string;
  city: string;
  state: string;
  zip: string;
};

type Props = {
  label: string;
  onSelect: (fields: AddressFields) => void;
};

/**
 * Dropdown button that searches the address book and auto-fills
 * origin/destination fields when a saved address is selected.
 */
export function AddressAutocomplete({ label, onSelect }: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<AddressBookEntry[]>([]);
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = React.useCallback(async (q: string) => {
    if (!q.trim()) {
      // Load recent/favorites on open
      try {
        setLoading(true);
        const data = await apiClient.getAddresses({ favorites: true });
        setResults(data.addresses.slice(0, 10));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const data = await apiClient.searchAddresses(q);
      setResults(data.results);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = React.useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void search(value);
      }, 250);
    },
    [search],
  );

  // Load favorites when opened
  React.useEffect(() => {
    if (open) {
      void search("");
    }
  }, [open, search]);

  const handleSelect = (entry: AddressBookEntry) => {
    onSelect({
      address: entry.address,
      city: entry.city,
      state: entry.state,
      zip: entry.zip,
    });
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <BookOpen className="size-3.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search saved addresses..."
            value={query}
            onValueChange={handleQueryChange}
          />
          <CommandList>
            {loading ? (
              <div className="py-4 text-center text-xs text-muted-foreground">
                Searching...
              </div>
            ) : results.length === 0 ? (
              <CommandEmpty>
                {query ? "No addresses found." : "No saved addresses yet."}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((entry) => (
                  <CommandItem
                    key={entry.id}
                    value={entry.id}
                    onSelect={() => handleSelect(entry)}
                    className="flex flex-col items-start gap-0.5 py-2"
                  >
                    <div className="flex w-full items-center gap-1.5">
                      {entry.isFavorite ? (
                        <Star className="size-3 shrink-0 fill-yellow-400 text-yellow-500" />
                      ) : null}
                      <span className="truncate text-sm font-medium">
                        {entry.name || entry.company || "Unnamed"}
                      </span>
                      {entry.isValidated ? (
                        <CheckCircle2 className="ml-auto size-3 shrink-0 text-emerald-600" />
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {entry.address}, {entry.city}, {entry.state} {entry.zip}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
